/**
 * "Search for match" -- scans the WHOLE journal for the segment whose `publicText` shares the most
 * lines with a draft's text.
 *
 * This is deliberately independent of `Match.ts`'s date/kind/ordinal scoring: that scoring answers
 * "which night is this post for" (and needs a date to work at all), while this answers "where does
 * this text already exist". They answer different questions, and a post whose date could not be
 * inferred at all is exactly the case where the text-overlap search is the only usable signal.
 *
 * Pure (no DB access), so the ranking can be unit-tested.
 */
import {GetPublicTextOverlap, JournalEntryForImport, PublicTextOverlap} from "./Match.js";

/** Which text field of a segment is compared. `publicText` is what this tool writes, so that is the
 * right analogue of the draft's text; the other two are only used as a fallback. */
export const CLOSEST_MATCH_FIELDS = ["publicText", "longText", "shortText"] as const;

export type ClosestSegmentMatch = PublicTextOverlap & {
	entryId: string;
	/** index within `entry.segments` (wake markers included, so it matches the DB array) */
	segmentIndex: number;
	segmentId: string|n;
	/** which of the segment's three text fields the overlap was measured against */
	field: typeof CLOSEST_MATCH_FIELDS[number];
	entryTitle: string|n;
};

/** Best-ranked match, or null when the journal has no segments at all. A match with
 * `matchedLines == 0` is still returned (the caller shows it as "no match"), because "we looked and
 * found nothing" is useful information; `allMatch` tells the two apart. */
export function FindClosestSegmentMatch(incomingText: string, entries: readonly JournalEntryForImport[]): ClosestSegmentMatch|null {
	let best: ClosestSegmentMatch|null = null;

	for (const entry of entries) {
		for (let segmentIndex = 0; segmentIndex < entry.segments.length; segmentIndex++) {
			const segment = entry.segments[segmentIndex];
			if (segment == null) continue;

			for (const field of CLOSEST_MATCH_FIELDS) {
				const existingText = segment[field] ?? "";
				// an empty field can never contain a line, so skip it rather than ranking a blank segment
				if (existingText.trim().length == 0) continue;

				const overlap = GetPublicTextOverlap(incomingText, existingText);
				const candidate: ClosestSegmentMatch = {
					...overlap,
					entryId: entry.id,
					segmentIndex,
					segmentId: segment.id ?? null,
					field,
					entryTitle: entry.title ?? null,
				};
				if (best == null || IsBetterMatch(candidate, best)) best = candidate;
			}
		}
	}

	return best;
}

/** Ranking: most shared lines wins. Ties are broken by the FEWEST extra lines (so "the text is exactly
 * this segment" beats "this segment contains this text plus a lot of other stuff"), then by the
 * earliest field in `CLOSEST_MATCH_FIELDS` (publicText > longText > shortText). */
function IsBetterMatch(a: ClosestSegmentMatch, b: ClosestSegmentMatch) {
	if (a.matchedLines != b.matchedLines) return a.matchedLines > b.matchedLines;
	if (a.extraExistingLines != b.extraExistingLines) return a.extraExistingLines < b.extraExistingLines;
	if (a.existingLines != b.existingLines) return a.existingLines < b.existingLines;
	return CLOSEST_MATCH_FIELDS.indexOf(a.field) < CLOSEST_MATCH_FIELDS.indexOf(b.field);
}
